export const NATIVE_LAUNCH_VARIANT_IDS = [
  "competitive",
  "brand",
  "social",
] as const;

export const NATIVE_LAUNCH_VARIANT_STORAGE_KEY = "ap-native-launch-variant";

export type NativeLaunchVariantId = (typeof NATIVE_LAUNCH_VARIANT_IDS)[number];

export type NativeLaunchVariantStorage = Pick<Storage, "getItem" | "setItem">;

export function pickNativeLaunchVariant(
  randomValue: number = Math.random(),
): NativeLaunchVariantId {
  const lastIndex = NATIVE_LAUNCH_VARIANT_IDS.length - 1;
  const unit = Number.isFinite(randomValue)
    ? Math.min(1, Math.max(0, randomValue))
    : 0;
  const index = Math.min(
    lastIndex,
    Math.floor(unit * NATIVE_LAUNCH_VARIANT_IDS.length),
  );

  return NATIVE_LAUNCH_VARIANT_IDS[index] ?? "competitive";
}

function isNativeLaunchVariantId(
  value: string | null,
): value is NativeLaunchVariantId {
  return (
    value !== null &&
    (NATIVE_LAUNCH_VARIANT_IDS as readonly string[]).includes(value)
  );
}

function readStoredNativeLaunchVariant(
  storage: NativeLaunchVariantStorage,
): NativeLaunchVariantId | null {
  try {
    const stored = storage.getItem(NATIVE_LAUNCH_VARIANT_STORAGE_KEY);
    return isNativeLaunchVariantId(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function resolveNativeLaunchVariant(
  storage: NativeLaunchVariantStorage | null,
  randomValue: number = Math.random(),
): NativeLaunchVariantId {
  const stored = storage ? readStoredNativeLaunchVariant(storage) : null;
  if (stored) {
    return stored;
  }

  const picked = pickNativeLaunchVariant(randomValue);
  if (!storage) {
    return picked;
  }

  try {
    storage.setItem(NATIVE_LAUNCH_VARIANT_STORAGE_KEY, picked);
  } catch {
    // Private mode can reject writes. The pick still holds for this mount.
  }

  return picked;
}

export function formatNativeLaunchShowDate(
  startTime: string,
  locale: string,
): string {
  const date = new Date(startTime);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}
